import { createOrUpdateReportConfig } from 'src/graphql/mutations';
import { listReportConfig } from 'src/graphql/queries';
import {
  GetReportDataFromS3Params,
  GetReportDataParams,
  GetReportDataResponse,
  MetadataFetchResult,
  ReportConfigurationMutationPayload,
  ReportDataWarning,
  ReportS3Metadata,
  ReportsConfigurationAPIResponse,
} from 'src/types/admin/reportsConfig.types';
import { getAllObjectKeysFromS3, GetFileFromS3Options, getFileFromS3URI, parseS3URI } from 'src/utils/aws-s3-services';
import { formatApiError } from 'src/utils/errorUtils';
import { logger } from 'src/utils/logger';
import { MetricCategory, publishCounter, publishTimer } from 'src/utils/metrics';
import { addClientRowIds } from 'src/utils/reportDataUtils';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * Metadata file name to exclude from folder-based fetches
 */
const METADATA_FILE_NAME = 'metadata.json';

/**
 * Cache control for hard refresh - bypasses all caches.
 * Used when metadata indicates data has changed.
 */
const NO_CACHE_CONTROL = 'no-cache, no-store, must-revalidate';

/**
 * Cache control for using browser/CloudFront cache.
 * Used when metadata is unchanged, allowing browser to serve cached data.
 */
const USE_CACHE_CONTROL = 'max-age=3600';

/**
 * localStorage key for storing report metadata cache.
 * This enables multi-report caching across page navigations.
 */
const METADATA_CACHE_STORAGE_KEY = 'verity_report_metadata_cache';

// ============================================================================
// localStorage Metadata Cache Utilities
// ============================================================================

/**
 * Structure for storing multiple reports' metadata in localStorage
 */
interface MetadataCacheStorage {
  [reportUniqueCode: string]: ReportS3Metadata;
}

/**
 * Retrieves cached metadata for a specific report from localStorage.
 * This enables metadata comparison without Redux state dependency.
 *
 * @param reportUniqueCode - The unique code of the report
 * @returns Cached metadata if found, null otherwise
 */
const getCachedMetadataFromStorage = (reportUniqueCode: string): ReportS3Metadata | null => {
  try {
    const cacheString = localStorage.getItem(METADATA_CACHE_STORAGE_KEY);
    if (!cacheString) return null;

    const cache: MetadataCacheStorage = JSON.parse(cacheString);
    return cache[reportUniqueCode] ?? null;
  } catch (error) {
    logger.warn('Failed to read metadata cache from localStorage', {
      reportUniqueCode,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return null;
  }
};

/**
 * Stores metadata for a specific report in localStorage.
 * This enables cross-navigation caching without Redux state changes.
 *
 * @param reportUniqueCode - The unique code of the report
 * @param metadata - The metadata to cache
 */
const setCachedMetadataInStorage = (reportUniqueCode: string, metadata: ReportS3Metadata): void => {
  try {
    const cacheString = localStorage.getItem(METADATA_CACHE_STORAGE_KEY);
    const cache: MetadataCacheStorage = cacheString ? JSON.parse(cacheString) : {};

    cache[reportUniqueCode] = metadata;

    localStorage.setItem(METADATA_CACHE_STORAGE_KEY, JSON.stringify(cache));
    logger.info('Stored metadata in localStorage cache', { reportUniqueCode, timestamp: metadata.timestamp });
  } catch (error) {
    // Silently fail - caching is best-effort, shouldn't break main functionality
    logger.warn('Failed to write metadata cache to localStorage', {
      reportUniqueCode,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

/**
 * Clears all cached metadata from localStorage.
 * Useful for cache invalidation or debugging.
 */
export const clearMetadataCache = (): void => {
  try {
    localStorage.removeItem(METADATA_CACHE_STORAGE_KEY);
    logger.info('Cleared all metadata cache from localStorage');
  } catch (error) {
    logger.warn('Failed to clear metadata cache from localStorage', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

/**
 * Maximum number of concurrent S3 file fetches
 * S3 supports 5,500+ GET requests per second per prefix, so we can safely
 * increase parallelism. Higher values reduce total fetch time for large datasets.
 * Set to 50 for optimal balance between speed and browser resource usage.
 */
const MAX_CONCURRENT_S3_FETCHES = 50;

/**
 * Options for batch processing with error handling capabilities
 */
interface BatchProcessingOptions<T, R> {
  /**
   * When true, continues processing remaining items even if some fail.
   * Failed items will be tracked in the errors array.
   * When false (default), throws on first error.
   */
  continueOnError?: boolean;
  /**
   * Optional callback for handling errors on individual items.
   * Can return a fallback value to use instead, or undefined to skip the item.
   * Only called when continueOnError is true.
   */
  onError?: (item: T, error: Error) => R | undefined;
  /**
   * Optional callback to extract an identifier from an item for error reporting.
   * Defaults to using the item's string representation.
   */
  getItemIdentifier?: (item: T) => string;
}

/**
 * Result of batch processing with error tracking
 */
interface BatchProcessingResult<R> {
  /** Successfully processed results */
  results: R[];
  /** Errors encountered during processing (only populated when continueOnError is true) */
  errors: Array<{ source: string; message: string }>;
}

/**
 * Processes items in batches with a maximum concurrency limit and optional error handling.
 *
 * Supports two modes:
 * 1. Fail-fast (default): Throws on first error, suitable for critical operations
 * 2. Graceful degradation: Continues on errors, returns partial results with error tracking
 *
 * @param items - Array of items to process
 * @param batchSize - Maximum number of concurrent operations
 * @param processor - Async function to process each item
 * @param options - Optional configuration for error handling
 * @returns Promise resolving to results and any errors encountered
 */
const processBatches = async <T, R>(
  items: T[],
  batchSize: number,
  processor: (item: T) => Promise<R>,
  options: BatchProcessingOptions<T, R> = {}
): Promise<BatchProcessingResult<R>> => {
  const { continueOnError = false, onError, getItemIdentifier = (item) => String(item) } = options;
  const results: R[] = [];
  const errors: Array<{ source: string; message: string }> = [];

  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);

    if (continueOnError) {
      // Process each item individually to catch errors per item
      const batchPromises = batch.map(async (item) => {
        try {
          return { success: true as const, result: await processor(item) };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          const itemId = getItemIdentifier(item);
          logger.warn(`Failed to process item: ${itemId}`, { error: errorMessage });

          // Try the onError callback for a fallback value
          if (onError) {
            const fallback = onError(item, error instanceof Error ? error : new Error(errorMessage));
            if (fallback !== undefined) {
              return { success: true as const, result: fallback };
            }
          }

          return { success: false as const, source: itemId, message: errorMessage };
        }
      });

      const batchResults = await Promise.all(batchPromises);

      for (const result of batchResults) {
        if (result.success) {
          results.push(result.result);
        } else {
          errors.push({ source: result.source, message: result.message });
        }
      }
    } else {
      // Fail-fast mode: use Promise.all which rejects on first error
      const batchResults = await Promise.all(batch.map(processor));
      results.push(...batchResults);
    }

    // Log progress for large datasets
    if (items.length > batchSize) {
      const batchNum = Math.floor(i / batchSize) + 1;
      const totalBatches = Math.ceil(items.length / batchSize);
      const errorInfo = errors.length > 0 ? `, ${errors.length} errors so far` : '';
      logger.info(`Processed batch ${batchNum} of ${totalBatches}${errorInfo}`);
    }
  }

  return { results, errors };
};

/**
 * Fetches all reports configuration from the GraphQL API
 * @returns Promise resolving to array of reports configuration
 * @throws Error if API call fails
 */
export const fetchReportsConfigAPI = async (): Promise<ReportsConfigurationAPIResponse[]> => {
  try {
    logger.info('Fetching reports configuration from GraphQL API');
    const result = await fetchGraphQLRequest<{ listReportConfig: ReportsConfigurationAPIResponse[] }>({
      query: listReportConfig,
      variables: {},
      requestName: 'listReportConfig',
      requestType: 'query',
    });

    const reportsConfig = result.listReportConfig || [];
    logger.info(`Fetched ${reportsConfig?.length || '-'} reports configuration from API`);
    return reportsConfig;
  } catch (error) {
    logger.error('Error fetching reports configuration from GraphQL API:', { error });
    throw formatApiError('Failed to fetch reports configuration', error);
  }
};

/**
 * Creates a new report configuration
 * @param payload - The report configuration data to create
 * @returns Promise resolving to the created report configuration
 * @throws Error if API call fails
 */
export const createReportConfigAPI = async (
  payload: ReportConfigurationMutationPayload
): Promise<{ success: boolean; message: string }> => {
  try {
    logger.info('Creating report configuration via GraphQL API', { payload });
    const result = await fetchGraphQLRequest<{
      createOrUpdateReportConfig: { numberOfRecordsUpdated: number };
    }>({
      query: createOrUpdateReportConfig,
      variables: { input: payload },
      requestName: 'createOrUpdateReportConfig',
      requestType: 'mutation',
      auditConfig: { pageName: 'report_config' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateReportConfig.numberOfRecordsUpdated || 0;
    logger.info('Report configuration created successfully', { numberOfRecordsUpdated });
    return {
      success: numberOfRecordsUpdated > 0,
      message: `Report configuration created successfully (${numberOfRecordsUpdated} records updated)`,
    };
  } catch (error) {
    logger.error('Error creating report configuration via GraphQL API:', { error });
    throw formatApiError('Failed to create report configuration', error);
  }
};

/**
 * Updates an existing report configuration
 * @param payload - The report configuration data to update (must include report_id)
 * @returns Promise resolving to the updated report configuration
 * @throws Error if API call fails
 */
export const updateReportConfigAPI = async (
  payload: ReportConfigurationMutationPayload
): Promise<{ success: boolean; message: string }> => {
  try {
    if (payload.report_id === null) {
      throw new Error('Report ID is required for update operation');
    }

    logger.info('Updating report configuration via GraphQL API', { payload });
    const result = await fetchGraphQLRequest<{
      createOrUpdateReportConfig: { numberOfRecordsUpdated: number };
    }>({
      query: createOrUpdateReportConfig,
      variables: { input: payload },
      requestName: 'createOrUpdateReportConfig',
      requestType: 'mutation',
      auditConfig: { pageName: 'report_config' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateReportConfig.numberOfRecordsUpdated || 0;
    logger.info('Report configuration updated successfully', { numberOfRecordsUpdated });
    return {
      success: numberOfRecordsUpdated > 0,
      message: `Report configuration updated successfully (${numberOfRecordsUpdated} records updated)`,
    };
  } catch (error) {
    logger.error('Error updating report configuration via GraphQL API:', { error });
    throw formatApiError('Failed to update report configuration', error);
  }
};

/**
 * Saves a report configuration (creates or updates based on report_id)
 * @param payload - The report configuration mutation payload
 * @returns Promise resolving to success status and message
 */
export const saveReportConfigAPI = async (
  payload: ReportConfigurationMutationPayload
): Promise<{ success: boolean; message: string }> => {
  if (payload.report_id === null) {
    return createReportConfigAPI(payload);
  }
  return updateReportConfigAPI(payload);
};

// ============================================================================
// Report Data Fetching Functions
// ============================================================================

/**
 * Checks if the S3 URI points to a direct JSON file
 * @param s3URI - The S3 URI to check
 * @returns True if the URI ends with .json (case-insensitive)
 */
const isDirectJsonFile = (s3URI: string): boolean => {
  return s3URI.toLowerCase().endsWith('.json');
};

/**
 * Compares two metadata objects to determine if data needs to be refreshed.
 *
 * Returns true (metadata changed) if:
 * - No cached metadata exists (first load)
 * - No new metadata exists (fallback to hard refresh)
 * - Timestamp has changed
 * - last_updated_by has changed
 * - glue_job_name has changed
 * - glue_job_region has changed
 *
 * @param cached - Previously cached metadata
 * @param current - Newly fetched metadata
 * @returns True if metadata changed (requires hard refresh), false if unchanged
 */
const hasMetadataChanged = (
  cached: ReportS3Metadata | null | undefined,
  current: ReportS3Metadata | undefined
): boolean => {
  // No cached metadata = first load, need to fetch data
  if (!cached) {
    return true;
  }

  // No current metadata = can't compare, assume changed for safety
  if (!current) {
    return true;
  }

  // Compare all relevant fields
  const timestampChanged = cached.timestamp !== current.timestamp;
  const updatedByChanged = cached.last_updated_by !== current.last_updated_by;
  const glueJobNameChanged = cached.glue_job_name !== current.glue_job_name;
  const glueJobRegionChanged = cached.glue_job_region !== current.glue_job_region;

  return timestampChanged || updatedByChanged || glueJobNameChanged || glueJobRegionChanged;
};

/**
 * Fetches metadata.json from S3 and compares with localStorage cached version.
 * Metadata is ALWAYS fetched fresh from S3 (no HTTP cache) to ensure freshness.
 * The comparison is done against localStorage cache, enabling multi-report caching.
 *
 * Flow:
 * 1. Read cached metadata from localStorage for this report
 * 2. Fetch fresh metadata from S3 (always no-cache)
 * 3. Compare fresh vs localStorage cached
 * 4. If metadata changed, update localStorage cache
 *
 * @param metadataURI - Full S3 URI to metadata.json file
 * @param reportUniqueCode - Report unique code for logging and cache key
 * @returns Metadata fetch result with comparison outcome
 */
const fetchAndCompareMetadata = async (
  metadataURI: string,
  reportUniqueCode: string,
  signal?: AbortSignal
): Promise<MetadataFetchResult> => {
  // Step 1: Read cached metadata from localStorage
  const cachedMetadata = getCachedMetadataFromStorage(reportUniqueCode);

  logger.info('Checking localStorage for cached metadata', {
    reportUniqueCode,
    hasCachedMetadata: !!cachedMetadata,
    cachedTimestamp: cachedMetadata?.timestamp,
  });

  // Step 2: ALWAYS fetch fresh metadata from S3 (no HTTP cache)
  let metadata: ReportS3Metadata | undefined;

  try {
    logger.info('Fetching metadata from S3 (no cache)', { reportUniqueCode, metadataURI });
    metadata = await getFileFromS3URI<ReportS3Metadata>(metadataURI, {
      cacheControl: NO_CACHE_CONTROL,
      signal,
    });
    logger.info('Successfully fetched report metadata from S3', { reportUniqueCode, metadata });
  } catch (error) {
    logger.info('No metadata.json found in S3 (this is optional)', {
      reportUniqueCode,
      metadataURI,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }

  // Step 3: Compare fresh metadata vs localStorage cached
  const metadataChanged = hasMetadataChanged(cachedMetadata, metadata);

  logger.info('Metadata comparison result (localStorage vs S3)', {
    reportUniqueCode,
    metadataChanged,
    localStorageTimestamp: cachedMetadata?.timestamp,
    s3Timestamp: metadata?.timestamp,
  });

  // Step 4: If metadata changed and we have fresh metadata, update localStorage cache
  if (metadataChanged && metadata) {
    setCachedMetadataInStorage(reportUniqueCode, metadata);
  }

  return { metadata, metadataChanged };
};

/**
 * Determines the appropriate cache control header based on metadata comparison.
 *
 * When metadata is unchanged:
 * - Use browser/CloudFront cache for data files
 * - Browser will return cached responses if available
 *
 * When metadata is changed:
 * - Bypass all caches (hard refresh)
 * - Ensures users always see fresh data
 *
 * @param metadataChanged - Whether metadata has changed from cached version
 * @returns Cache control header to use for data fetches
 */
const getCacheControlForData = (metadataChanged: boolean): string => {
  return metadataChanged ? NO_CACHE_CONTROL : USE_CACHE_CONTROL;
};

/**
 * Fetches report data from a single JSON file in S3 using metadata-first approach
 * with localStorage-based multi-report caching.
 *
 * Flow:
 * 1. Fetch fresh metadata.json from S3 (always no HTTP cache)
 * 2. Compare with localStorage cached metadata for this report
 * 3. If metadata changed → Fetch data WITHOUT cache (hard refresh)
 * 4. If metadata unchanged → Fetch data WITH browser cache (fast from CloudFront)
 *
 * @param reportUniqueCode - The report unique code
 * @param s3URI - The S3 URI pointing to the JSON file
 * @returns Promise resolving to report data response
 * @throws Error if S3 fetch fails
 */
const getReportDataFromS3DirectFile = async (
  reportUniqueCode: string,
  s3URI: string,
  signal?: AbortSignal
): Promise<GetReportDataResponse> => {
  logger.info('Fetching report data from direct S3 file (metadata-first with localStorage)', {
    reportUniqueCode,
    s3URI,
  });

  // Derive the metadata.json path from the data file path
  const { bucket, s3Key } = parseS3URI(s3URI);
  const folderPath = s3Key.substring(0, s3Key.lastIndexOf('/'));
  const metadataURI = `s3://${bucket}/${folderPath}/${METADATA_FILE_NAME}`;

  // Step 1 & 2: Fetch fresh metadata from S3 and compare with localStorage
  const { metadata, metadataChanged } = await fetchAndCompareMetadata(metadataURI, reportUniqueCode, signal);

  // Step 3 & 4: Determine cache control based on metadata comparison
  const dataCacheControl = getCacheControlForData(metadataChanged);

  logger.info('Fetching data from S3', {
    reportUniqueCode,
    cacheControl: dataCacheControl,
    metadataChanged,
    useBrowserCache: !metadataChanged,
  });

  const s3Data = await getFileFromS3URI<{ data: Record<string, unknown>[] }>(s3URI, {
    cacheControl: dataCacheControl,
    signal,
  });

  // Add client-generated row IDs for AG-Grid row identification
  const rawData = Array.isArray(s3Data.data) ? s3Data.data : [];
  const dataWithRowIds = addClientRowIds(rawData);

  const rowCount = dataWithRowIds.length;
  const dataSizeBytes = new TextEncoder().encode(JSON.stringify(rawData)).length;

  logger.info(`Fetched ${rowCount} records from direct S3 file for report ${reportUniqueCode}`, {
    dataSizeBytes,
    cacheHit: !metadataChanged,
  });

  publishCounter('ReportDataFetched', rowCount, MetricCategory.BUSINESS, {
    reportCode: reportUniqueCode,
    fetchType: 'direct_file',
  });
  publishCounter('ReportDataSizeBytes', dataSizeBytes, MetricCategory.PERFORMANCE, { reportCode: reportUniqueCode });
  publishCounter('ReportCacheHit', metadataChanged ? 0 : 1, MetricCategory.PERFORMANCE, {
    reportCode: reportUniqueCode,
  });

  return {
    reportUniqueCode,
    data: dataWithRowIds,
    metadata,
  };
};

/**
 * Fetches report data from a folder in S3 containing multiple JSON files.
 * Uses metadata-first approach with localStorage-based multi-report caching.
 *
 * Flow:
 * 1. Fetch fresh metadata.json from S3 (always no HTTP cache)
 * 2. Compare with localStorage cached metadata for this report
 * 3. If metadata changed → Fetch data files WITHOUT cache (hard refresh)
 * 4. If metadata unchanged → Fetch data files WITH browser cache (fast from CloudFront)
 *
 * @param reportUniqueCode - The report unique code
 * @param s3URI - The S3 URI pointing to the folder (prefix)
 * @returns Promise resolving to combined report data response
 * @throws Error if S3 operations fail
 */
const getReportDataFromS3Folder = async (
  reportUniqueCode: string,
  s3URI: string,
  signal?: AbortSignal
): Promise<GetReportDataResponse> => {
  const fetchStartTime = Date.now();
  logger.info('Fetching report data from S3 folder (metadata-first with localStorage)', { reportUniqueCode, s3URI });

  const { bucket, s3Key } = parseS3URI(s3URI);

  // Ensure the prefix ends with / for proper folder listing
  const folderPrefix = s3Key.endsWith('/') ? s3Key : `${s3Key}/`;

  // Step 1 & 2: Fetch fresh metadata from S3 and compare with localStorage
  const metadataURI = `s3://${bucket}/${folderPrefix}${METADATA_FILE_NAME}`;
  const { metadata, metadataChanged } = await fetchAndCompareMetadata(metadataURI, reportUniqueCode, signal);

  // Step 3 & 4: Determine cache control based on metadata comparison
  const dataCacheControl = getCacheControlForData(metadataChanged);

  logger.info('Fetching data files from S3', {
    reportUniqueCode,
    cacheControl: dataCacheControl,
    metadataChanged,
    useBrowserCache: !metadataChanged,
  });

  // List all keys in the folder
  const allKeys = await getAllObjectKeysFromS3(bucket, folderPrefix, signal);

  logger.info(`Found ${allKeys.length} keys in S3 folder`, {
    reportUniqueCode,
    bucket,
    folderPrefix,
    allKeys: allKeys.slice(0, 20), // Log first 20 keys for debugging
  });

  // Filter out metadata.json and keep only JSON files
  const dataFileKeys = allKeys.filter((key) => {
    const fileName = key.split('/').pop() || '';
    return fileName.toLowerCase().endsWith('.json') && fileName.toLowerCase() !== METADATA_FILE_NAME;
  });

  logger.info(`Filtered to ${dataFileKeys.length} data files (excluding ${METADATA_FILE_NAME})`, {
    reportUniqueCode,
    dataFileKeys: dataFileKeys.slice(0, 10), // Log first 10 data files for debugging
  });

  if (dataFileKeys.length === 0) {
    logger.warn('No data files found in S3 folder', { reportUniqueCode, s3URI });
    return {
      reportUniqueCode,
      data: [],
      metadata,
    };
  }

  // Fetch all files in batches with graceful degradation
  const s3Options: GetFileFromS3Options = {
    cacheControl: dataCacheControl,
    signal,
  };

  const fetchFileData = async (key: string): Promise<Record<string, unknown>[]> => {
    const fileURI = `s3://${bucket}/${key}`;
    const fileData = await getFileFromS3URI<{ data: Record<string, unknown>[] }>(fileURI, s3Options);
    return Array.isArray(fileData.data) ? fileData.data : [];
  };

  const { results: allDataArrays, errors: fetchErrors } = await processBatches(
    dataFileKeys,
    MAX_CONCURRENT_S3_FETCHES,
    fetchFileData,
    {
      continueOnError: true,
      getItemIdentifier: (key) => key,
      onError: (key, error) => {
        logger.error(`Failed to fetch file from S3`, { key, error: error.message });
        return undefined;
      },
    }
  );

  // Convert fetch errors to warnings for the response
  const warnings: ReportDataWarning[] = fetchErrors.map((err) => ({
    source: err.source,
    message: `Failed to load file: ${err.message}`,
  }));

  if (warnings.length > 0) {
    logger.warn(`${warnings.length} files failed to load for report ${reportUniqueCode}`, {
      failedFiles: warnings.map((w) => w.source),
    });
  }

  // Combine all data arrays into one
  const combinedData: Record<string, unknown>[] = allDataArrays.flat();

  // Add client-generated row IDs for AG-Grid row identification
  const dataWithRowIds = addClientRowIds(combinedData);

  const totalDurationMs = Date.now() - fetchStartTime;
  const totalDurationSeconds = (totalDurationMs / 1000).toFixed(2);
  const successCount = dataFileKeys.length - warnings.length;
  const dataSizeBytes = new TextEncoder().encode(JSON.stringify(combinedData)).length;

  logger.info(
    `Fetched ${dataWithRowIds.length} total records from ${successCount}/${dataFileKeys.length} files for report ${reportUniqueCode}. Total fetch time: ${totalDurationSeconds}s`,
    { dataSizeBytes, cacheHit: !metadataChanged, failedFiles: warnings.length }
  );

  publishTimer('ReportDataFetchTime', totalDurationMs, MetricCategory.PERFORMANCE, {
    reportCode: reportUniqueCode,
    fetchType: 'folder',
  });
  publishCounter('ReportDataFetched', dataWithRowIds.length, MetricCategory.BUSINESS, {
    reportCode: reportUniqueCode,
    fetchType: 'folder',
  });
  publishCounter('ReportDataSizeBytes', dataSizeBytes, MetricCategory.PERFORMANCE, { reportCode: reportUniqueCode });
  publishCounter('ReportFilesTotal', dataFileKeys.length, MetricCategory.PERFORMANCE, { reportCode: reportUniqueCode });
  publishCounter('ReportFilesLoaded', successCount, MetricCategory.PERFORMANCE, { reportCode: reportUniqueCode });
  publishCounter('ReportCacheHit', metadataChanged ? 0 : 1, MetricCategory.PERFORMANCE, {
    reportCode: reportUniqueCode,
  });
  if (warnings.length > 0) {
    publishCounter('ReportFilesFailed', warnings.length, MetricCategory.ERROR, { reportCode: reportUniqueCode });
  }

  return {
    reportUniqueCode,
    data: dataWithRowIds,
    metadata,
    ...(warnings.length > 0 && { warnings }),
  };
};

/**
 * Fetches report data from S3 location using metadata-first caching strategy
 * with localStorage-based multi-report metadata cache.
 *
 * Supports two modes:
 * 1. Direct JSON file: If s3URI ends with .json, fetches the single file directly
 * 2. Folder with multiple files: If s3URI doesn't end with .json, lists all keys
 *    in the folder, excludes metadata.json, and fetches all files in parallel
 *
 * Caching Strategy:
 * 1. ALWAYS fetch metadata.json fresh from S3 (no HTTP cache)
 * 2. Compare with localStorage cached metadata for this specific report
 * 3. If metadata changed → Update localStorage and fetch data WITHOUT cache
 * 4. If metadata same → Fetch data WITH browser cache (CloudFront serves cached)
 *
 * @param params - Parameters including reportUniqueCode and s3URI
 * @returns Promise resolving to report data response with _row_id added to each record
 * @throws Error if S3 fetch fails
 */
export const getReportDataFromS3 = async (params: GetReportDataFromS3Params): Promise<GetReportDataResponse> => {
  const { reportUniqueCode, s3URI, signal } = params;

  try {
    logger.info('Fetching report data from S3 (metadata-first with localStorage)', {
      reportUniqueCode,
      s3URI,
    });

    if (!s3URI) {
      throw new Error('S3 URI is required for S3 location type');
    }

    // Determine fetch strategy based on URL pattern
    if (isDirectJsonFile(s3URI)) {
      return await getReportDataFromS3DirectFile(reportUniqueCode, s3URI, signal);
    } else {
      return await getReportDataFromS3Folder(reportUniqueCode, s3URI, signal);
    }
  } catch (error) {
    logger.error('Error fetching report data from S3:', { error, reportUniqueCode, s3URI });
    throw formatApiError('Failed to fetch report data from S3', error);
  }
};

/**
 * Fetches report data based on the report location type.
 * Uses metadata-first caching strategy with localStorage + browser/CloudFront cache.
 *
 * @param params - Parameters including reportUniqueCode, reportLocationType, etc.
 * @returns Promise resolving to report data response
 * @throws Error if fetch fails
 */
export const getReportDataAPI = async (params: GetReportDataParams): Promise<GetReportDataResponse> => {
  const { reportUniqueCode, reportLocationType, reportLocationDetails, signal } = params;

  logger.info('Fetching report data (metadata-first with localStorage + browser cache)', {
    reportUniqueCode,
    reportLocationType,
  });

  switch (reportLocationType) {
    case 'S3':
      return getReportDataFromS3({
        reportUniqueCode,
        s3URI: reportLocationDetails?.report_location_url ?? null,
        signal,
      });

    default:
      throw new Error(`Unsupported report location type: ${reportLocationType}`);
  }
};
